import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  EVENT_MODULE_KEYS,
  RELEASE_CAPABILITIES,
  createRecommendedHomeLayout,
  mergeEventHomeLayout,
  getEventModuleLabel,
  isEventModuleEnabled,
  resolveEventModules,
  type ConferenceEvent,
  type EventAppModules,
  type EventHomeLayout,
  type EventPhase,
  type HomeBlockKey,
  type EventModuleKey,
} from '@conference/shared';
import { ChevronDown, ChevronUp, Eye, RotateCcw, SlidersHorizontal } from 'lucide-react';
import apiClient from '../services/apiClient';
import { Button, ConfirmDialog, PageHeader, Spinner } from '../components/ui';
import {
  moveEventModule,
  normalizeEventModules,
  resetEventModule,
  updateEventModule,
} from '../utils/eventModuleForm';
import { HomeLayoutPreview } from '../components/HomeLayoutPreview';
import { AgendaFilterEditor } from '../components/AgendaFilterEditor';
import { CustomHomeSectionEditor } from '../components/CustomHomeSectionEditor';
import { eventContentService } from '../services/eventContentService';
import { buildAgendaFilterDraft } from '../utils/eventModuleForm';
import {
  homeLayoutSignature,
  canMoveCustomHomeSection,
  normalizeEventHomeLayout,
  moveCustomHomeSection,
  moveHomeBlock,
  addCustomHomeSection,
  deleteCustomHomeSection,
  duplicateCustomHomeSection,
  orderedHomeBlockKeys,
  resetHomeLayout,
  toggleQuickAction,
  updateCustomHomeSection,
  updateHomeBlock,
} from '../utils/eventHomeLayoutForm';
import { announceEventWorkspaceUpdated } from '../utils/eventWorkspace';

const descriptions: Record<EventModuleKey, string> = {
  agenda: 'Schedule, sessions, and session details.',
  papers: 'Research papers, downloads, and paper details.',
  speakers: 'Speaker directory and profiles.',
  exhibitors: 'General exhibitor and vendor directory.',
  sponsors: 'Sponsor directory grouped by sponsorship tier.',
  industry_partners: 'Organizations participating in the industry mixer.',
  committee: 'Organizing committee groups and members.',
  attendees: 'Opt-in attendee directory and profiles.',
  venue_map: 'Venue map and location shortcuts.',
  messaging: 'Attendee-to-attendee conversations.',
  ai_copilot: 'AI assistant, insights, and preparation tools.',
  notes: 'Personal notes attached to conference content.',
  recap: 'Conference recap and saved highlights.',
};

const contentCountPaths: Partial<Record<EventModuleKey, (eventId: string) => string>> = {
  agenda: (eventId) => `/sessions?event_id=${eventId}`,
  papers: (eventId) => `/papers?event_id=${eventId}`,
  speakers: (eventId) => `/speakers/all?event_id=${eventId}`,
  exhibitors: (eventId) => `/exhibitors?event_id=${eventId}`,
  sponsors: (eventId) => `/events/${eventId}/sponsors`,
  industry_partners: (eventId) => `/events/${eventId}/industry-partners`,
  committee: (eventId) => `/events/${eventId}/committee`,
  attendees: (eventId) => `/admin/attendees?event_id=${eventId}`,
};

function modulesSignature(modules: EventAppModules): string {
  return JSON.stringify(Object.entries(modules).sort(([left], [right]) => left.localeCompare(right)));
}

const homeBlockLabels: Record<HomeBlockKey, string> = {
  event_hero: 'Event hero', quick_actions: 'Quick actions', announcements: 'Announcements',
  happening_now: 'Happening now', up_next: 'Up next', featured_speakers: 'Featured speakers',
  featured_papers: 'Featured papers', featured_exhibitors: 'Featured exhibitors', recap: 'Conference recap',
};

const quickActionKeys: EventModuleKey[] = [
  'agenda', 'papers', 'speakers', 'exhibitors', 'sponsors', 'industry_partners', 'committee',
  'attendees', 'venue_map', 'notes', 'recap',
];

function applyReleaseModulePolicy(modules: EventAppModules): EventAppModules {
  if (RELEASE_CAPABILITIES.peerMessagingAvailable) return modules;
  return {
    ...modules,
    messaging: { ...modules.messaging, enabled: false },
  };
}

const recommendedHomeBlocks = createRecommendedHomeLayout().blocks ?? {};

export default function EventAppSetupPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentEventIdRef = useRef(eventId);
  currentEventIdRef.current = eventId;
  const [event, setEvent] = useState<ConferenceEvent | null>(null);
  const [draft, setDraft] = useState<EventAppModules>({});
  const [homeDraft, setHomeDraft] = useState<EventHomeLayout>({});
  const [savedSignature, setSavedSignature] = useState(modulesSignature({}));
  const [savedHomeSignature, setSavedHomeSignature] = useState(homeLayoutSignature({}));
  const activeTab: 'modules' | 'home' = searchParams.get('section') === 'home' ? 'home' : 'modules';
  const selectSection = (section: 'capabilities' | 'home') => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('section', section);
    setSearchParams(nextSearchParams, { replace: true });
  };
  const [previewPhase, setPreviewPhase] = useState<EventPhase>('during');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [contentCounts, setContentCounts] = useState<Partial<Record<EventModuleKey, number>>>({});
  const [agendaSessionTypes, setAgendaSessionTypes] = useState<string[]>([]);
  const [hasAnnouncements, setHasAnnouncements] = useState<boolean | undefined>(undefined);
  const pendingRemovedAssetIds = useRef(new Set<string>());

  const dirty = modulesSignature(draft) !== savedSignature || homeLayoutSignature(homeDraft) !== savedHomeSignature;
  const resolved = useMemo(
    () => resolveEventModules(draft).filter((module) => (
      module.key !== 'messaging' || RELEASE_CAPABILITIES.peerMessagingAvailable
    )),
    [draft],
  );
  const enabledModules = resolved.filter((module) => module.enabled);
  const prepSessionTypes = useMemo(
    () => buildAgendaFilterDraft(agendaSessionTypes, draft.agenda?.filters ?? []).filter((filter) => filter.enabled !== false),
    [agendaSessionTypes, draft.agenda?.filters],
  );
  const hasInvalidLabel = EVENT_MODULE_KEYS.some((key) => {
    const label = draft[key]?.label;
    return typeof label === 'string' && (!label.trim() || label.length > 40);
  });
  const hasInvalidAgendaFilter = (draft.agenda?.filters ?? []).some((filter) => (
    !filter.type.trim() || (typeof filter.label === 'string' && (!filter.label.trim() || filter.label.length > 40))
  ));
  const hasInvalidHomeTitle = Object.values(homeDraft.blocks ?? {}).some((block) => (
    typeof block.title === 'string' && (!block.title.trim() || block.title.length > 80)
  ));
  const hasInvalidCustomSection = (homeDraft.custom_sections ?? []).some((section) => (
    !section.title.trim() || !section.body_markdown.trim() || section.visibility.length === 0
    || Boolean(section.image_url) !== Boolean(section.image_alt?.trim())
    || (section.action?.kind === 'external_url' && !/^https?:\/\//i.test(section.action.url))
    || Boolean(section.action && !section.action.label.trim())
  ));
  const isLegacyHome = !homeDraft.version;
  const orderedHomeKeys = useMemo(() => orderedHomeBlockKeys(homeDraft), [homeDraft]);

  useEffect(() => {
    if (!eventId) {
      setError('Event ID is missing.');
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setEvent(null);
    setDraft({});
    setHomeDraft({});
    setSavedSignature(modulesSignature({}));
    setSavedHomeSignature(homeLayoutSignature({}));
    setContentCounts({});
    setAgendaSessionTypes([]);
    setHasAnnouncements(undefined);
    setSaving(false);
    setSaved(false);
    setError(null);
    apiClient.get<ConferenceEvent>(`/admin/events/${eventId}`)
      .then((loaded) => {
        if (!active) return;
        const modules = applyReleaseModulePolicy(normalizeEventModules(loaded.app_modules));
        const homeLayout = normalizeEventHomeLayout(loaded.home_layout);
        setEvent(loaded);
        setDraft(modules);
        setHomeDraft(homeLayout);
        setSavedSignature(modulesSignature(modules));
        setSavedHomeSignature(homeLayoutSignature(homeLayout));
        setError(null);
        const countRequests = Object.entries(contentCountPaths).map(async ([key, buildPath]) => {
          const rows = await apiClient.get<unknown>(buildPath(eventId));
          const count = Array.isArray(rows)
            ? rows.length
            : key === 'sponsors' && rows && typeof rows === 'object' && Array.isArray((rows as { sponsors?: unknown[] }).sponsors)
              ? (rows as { sponsors: unknown[] }).sponsors.length
              : key === 'industry_partners' && rows && typeof rows === 'object' && Array.isArray((rows as { partners?: unknown[] }).partners)
                ? (rows as { partners: unknown[] }).partners.length
                : key === 'committee' && rows && typeof rows === 'object' && Array.isArray((rows as { groups?: unknown[] }).groups)
                  ? (rows as { groups: unknown[] }).groups.length
                : undefined;
          const types = key === 'agenda' && Array.isArray(rows)
            ? rows.flatMap((row) => row && typeof row === 'object' && typeof (row as { type?: unknown }).type === 'string'
              ? [(row as { type: string }).type]
              : [])
            : [];
          return [key as EventModuleKey, count, types] as const;
        });
        void Promise.allSettled(countRequests).then((results) => {
          if (!active) return;
          const counts: Partial<Record<EventModuleKey, number>> = {};
          for (const result of results) {
            if (result.status === 'fulfilled' && result.value[1] !== undefined) {
              counts[result.value[0]] = result.value[1];
            }
            if (result.status === 'fulfilled' && result.value[0] === 'agenda') {
              setAgendaSessionTypes([...new Set(result.value[2])]);
            }
          }
          setContentCounts(counts);
        });
        void apiClient.get<unknown[]>(`/admin/notifications?event_id=${eventId}`)
          .then((history) => {
            if (active && Array.isArray(history)) setHasAnnouncements(history.length > 0);
          })
          .catch(() => {
            if (active) setHasAnnouncements(false);
          });
      })
      .catch((loadError) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'Could not load event.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [eventId]);

  useEffect(() => {
    if (!dirty) return undefined;
    const protectUnsavedChanges = (unloadEvent: BeforeUnloadEvent) => {
      unloadEvent.preventDefault();
      unloadEvent.returnValue = '';
    };
    window.addEventListener('beforeunload', protectUnsavedChanges);
    return () => window.removeEventListener('beforeunload', protectUnsavedChanges);
  }, [dirty]);

  function editModule(key: EventModuleKey, edit: Parameters<typeof updateEventModule>[2]) {
    setDraft((current) => updateEventModule(current, key, edit));
    setSaved(false);
    setError(null);
  }

  function queueOldHomeAsset(url: string | null | undefined) {
    const match = url?.match(/\/home-assets\/([0-9a-f-]+)\.png$/i);
    if (match) pendingRemovedAssetIds.current.add(match[1]);
  }

  function editCustomSection(section: Parameters<typeof updateCustomHomeSection>[2] & { id: string }) {
    setHomeDraft((current) => {
      const previous = current.custom_sections?.find(({ id }) => id === section.id);
      if (previous?.image_url && previous.image_url !== section.image_url) queueOldHomeAsset(previous.image_url);
      return updateCustomHomeSection(current, section.id, section);
    });
    setSaved(false);
  }

  async function uploadCustomSectionImage(sectionId: string, file: File) {
    if (!eventId) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const uploaded = await eventContentService.uploadHomeImage(eventId, formData);
      const existing = homeDraft.custom_sections?.find(({ id }) => id === sectionId);
      editCustomSection({
        ...existing!,
        id: sectionId,
        image_url: uploaded.image_url,
        image_alt: existing?.image_alt?.trim() || existing?.title || file.name,
      });
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Could not upload home image.');
    }
  }

  async function save() {
    if (!eventId || event?.id !== eventId || hasInvalidLabel || hasInvalidAgendaFilter || hasInvalidHomeTitle || hasInvalidCustomSection) return;
    const requestedEventId = eventId;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const updated = await apiClient.patch<ConferenceEvent>(`/admin/events/${requestedEventId}`, {
        app_modules: applyReleaseModulePolicy(draft),
        home_layout: homeDraft,
      });
      if (currentEventIdRef.current !== requestedEventId || updated.id !== requestedEventId) return;
      if (!Object.prototype.hasOwnProperty.call(updated, 'home_layout')) {
        throw new Error('The server did not persist the home page layout. Restart the API and apply the latest database migrations, then try again.');
      }
      const persisted = applyReleaseModulePolicy(normalizeEventModules(updated.app_modules ?? draft));
      const persistedHome = normalizeEventHomeLayout(updated.home_layout);
      if (homeLayoutSignature(persistedHome) !== homeLayoutSignature(homeDraft)) {
        throw new Error('The server did not persist the home page layout. Restart the API and apply the latest database migrations, then try again.');
      }
      setEvent(updated);
      announceEventWorkspaceUpdated(updated);
      setDraft(persisted);
      setHomeDraft(persistedHome);
      setSavedSignature(modulesSignature(persisted));
      setSavedHomeSignature(homeLayoutSignature(persistedHome));
      setSaved(true);
      setConfirmSaveOpen(false);
      const staleAssets = [...pendingRemovedAssetIds.current];
      pendingRemovedAssetIds.current.clear();
      void Promise.allSettled(staleAssets.map((assetId) => eventContentService.deleteHomeImage(requestedEventId, assetId)));
    } catch (saveError) {
      if (currentEventIdRef.current === requestedEventId) {
        setError(saveError instanceof Error ? saveError.message : 'Could not save attendee experience.');
        setConfirmSaveOpen(false);
      }
    } finally {
      if (currentEventIdRef.current === requestedEventId) setSaving(false);
    }
  }

  if (loading) {
    return <div className="flex justify-center py-24"><Spinner className="w-6 h-6 text-gray-500" /></div>;
  }

  if (!event) {
    return <div role="alert" className="m-8 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{error ?? 'Event not found.'}</div>;
  }

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        title="Attendee experience"
        description="Choose what attendees see for this event. Existing event data is never deleted."
        action={(
          <Button
            onClick={() => setConfirmSaveOpen(true)}
            loading={saving}
            disabled={!dirty || hasInvalidLabel || hasInvalidAgendaFilter || hasInvalidHomeTitle || hasInvalidCustomSection || event.id !== eventId}
            icon={<SlidersHorizontal size={14} />}
            aria-label="Save attendee experience"
          >
            Save changes
          </Button>
        )}
      />

      <div className="mb-6 rounded-xl border border-gray-800 bg-gray-900 p-4">
        <p className="text-xs uppercase tracking-wider text-gray-500">Configuring event</p>
        <p className="mt-1 text-sm font-medium text-white">{event.name}</p>
      </div>

      {error && (
        <div role="alert" className="mb-5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}
      {saved && (
        <div className="mb-5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
          Attendee experience saved.
        </div>
      )}

      <div role="tablist" aria-label="Attendee experience sections" className="mb-6 flex gap-2 border-b border-gray-800">
        <button type="button" role="tab" aria-selected={activeTab === 'modules'} onClick={() => selectSection('capabilities')} className={`px-4 py-2 text-sm font-medium ${activeTab === 'modules' ? 'border-b-2 border-sky-400 text-sky-300' : 'text-gray-400'}`}>Capabilities</button>
        <button type="button" role="tab" aria-selected={activeTab === 'home'} onClick={() => selectSection('home')} className={`px-4 py-2 text-sm font-medium ${activeTab === 'home' ? 'border-b-2 border-sky-400 text-sky-300' : 'text-gray-400'}`}>Home Page</button>
      </div>

      {activeTab === 'modules' && <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <section className="space-y-3" aria-label="Event capabilities">
          <div className="rounded-xl border border-gray-800 bg-gray-900 px-4 py-3">
            <p className="text-sm font-medium text-white">Choose what attendees can use and how those destinations are named.</p>
            <p className="mt-1 text-xs text-gray-500">Turning a capability off hides it without deleting its event content.</p>
          </div>
          {resolved.map((module, index) => (
            <article key={module.key} className="rounded-xl border border-gray-800 bg-gray-900 p-4">
              <div className="flex items-start gap-4">
                <label className="mt-1 inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    aria-label={`Show ${getEventModuleLabel(undefined, module.key)}`}
                    checked={isEventModuleEnabled(draft, module.key)}
                    onChange={(changeEvent) => editModule(module.key, { enabled: changeEvent.target.checked })}
                    className="h-4 w-4 accent-sky-500"
                  />
                </label>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-white">{module.label}</h2>
                    {!module.enabled && <span className="rounded bg-gray-800 px-2 py-0.5 text-[11px] text-gray-500">Hidden</span>}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{descriptions[module.key]}</p>
                  {contentCounts[module.key] === 0 && (
                    <p className="mt-2 text-xs text-amber-300">
                      No {getEventModuleLabel(undefined, module.key).toLowerCase()} have been added yet. Consider hiding this module.
                    </p>
                  )}
                  <label className="mt-3 block text-xs font-medium text-gray-400">
                    <span>{getEventModuleLabel(undefined, module.key)} label</span>
                    <input
                      aria-label={`${getEventModuleLabel(undefined, module.key)} label`}
                      value={getEventModuleLabel(draft, module.key)}
                      maxLength={40}
                      onChange={(changeEvent) => editModule(module.key, { label: changeEvent.target.value })}
                      className="mt-1.5 w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 focus:border-sky-500 focus:outline-none"
                    />
                  </label>
                </div>

                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    aria-label={`Move ${module.label} up`}
                    disabled={index === 0}
                    onClick={() => setDraft((current) => moveEventModule(current, module.key, 'up'))}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white disabled:opacity-25"
                  ><ChevronUp size={15} /></button>
                  <button
                    type="button"
                    aria-label={`Move ${module.label} down`}
                    disabled={index === resolved.length - 1}
                    onClick={() => setDraft((current) => moveEventModule(current, module.key, 'down'))}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white disabled:opacity-25"
                  ><ChevronDown size={15} /></button>
                  <button
                    type="button"
                    aria-label={`Use default settings for ${getEventModuleLabel(undefined, module.key)}`}
                    onClick={() => setDraft((current) => resetEventModule(current, module.key))}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white"
                  ><RotateCcw size={14} /></button>
                </div>
              </div>
              {module.key === 'agenda' && <AgendaFilterEditor
                filters={buildAgendaFilterDraft(agendaSessionTypes, draft.agenda?.filters ?? [])}
                onChange={(filters) => editModule('agenda', { filters })}
                onReset={() => {
                  setDraft((current) => {
                    const next = normalizeEventModules(current);
                    if (next.agenda) {
                      const agenda = { ...next.agenda };
                      delete agenda.filters;
                      next.agenda = agenda;
                    }
                    return next;
                  });
                  setSaved(false);
                }}
              />}
              {module.key === 'ai_copilot' && <div className="mt-4 border-t border-gray-800 pt-4">
                <label className="flex items-center gap-2 text-sm text-gray-200">
                  <input type="checkbox" aria-label="Enable Paper AI" checked={draft.ai_copilot?.paper_ai !== false} onChange={(changeEvent) => editModule('ai_copilot', { paper_ai: changeEvent.target.checked })} className="h-4 w-4 accent-sky-500" />
                  Paper AI insights and questions
                </label>
                <p className="mt-1 text-xs text-gray-500">Controls AI Insights and Ask AI on paper pages.</p>
                <fieldset className="mt-4">
                  <legend className="text-sm text-gray-200">Prep with AI session types</legend>
                  <p className="mt-1 text-xs text-gray-500">Choose which session types show Prep with AI.</p>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {prepSessionTypes.map((filter) => {
                      const selected = draft.ai_copilot?.session_prep_types;
                      const checked = !Array.isArray(selected) || selected.includes(filter.type);
                      return <label key={filter.type} className="flex items-center gap-2 text-sm text-gray-300">
                        <input type="checkbox" checked={checked} onChange={(changeEvent) => {
                          setDraft((current) => {
                            const currentTypes = Array.isArray(current.ai_copilot?.session_prep_types) ? current.ai_copilot.session_prep_types : prepSessionTypes.map(({ type }) => type);
                            const session_prep_types = changeEvent.target.checked ? [...new Set([...currentTypes, filter.type])] : currentTypes.filter((type) => type !== filter.type);
                            return updateEventModule(current, 'ai_copilot', { session_prep_types });
                          });
                          setSaved(false);
                          setError(null);
                        }} className="h-4 w-4 accent-sky-500" />
                        {filter.label}
                      </label>;
                    })}
                  </div>
                </fieldset>
              </div>}
            </article>
          ))}
        </section>

        <aside aria-label="Attendee preview" className="h-fit rounded-2xl border border-sky-500/20 bg-gray-900 p-5 xl:sticky xl:top-8">
          <div className="mb-4 flex items-center gap-2 text-sky-400">
            <Eye size={16} />
            <h2 className="text-sm font-semibold">Attendee preview</h2>
          </div>
          <p className="mb-4 text-xs text-gray-500">This order is used in attendee destinations such as Explore and Library. Home Page order is configured separately.</p>
          <div className="flex flex-wrap gap-2">
            {enabledModules.map((module) => (
              <span key={module.key} className="rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-xs text-gray-300">
                {module.label}
              </span>
            ))}
            {enabledModules.length === 0 && <span className="text-xs text-amber-300">No attendee modules enabled.</span>}
          </div>
        </aside>
      </div>}

      {activeTab === 'home' && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <section className="space-y-4" aria-label="Home page blocks">
            <div className="flex items-center justify-between rounded-xl border border-gray-800 bg-gray-900 p-4">
              <div><p className="text-sm font-medium text-white">Custom sections</p><p className="text-xs text-gray-500">Add formatted text, an optional image, and an optional action button.</p></div>
              <Button onClick={() => { setHomeDraft((current) => addCustomHomeSection(current)); setSaved(false); }}>Add section</Button>
            </div>
            {(homeDraft.custom_sections ?? []).map((section) => <CustomHomeSectionEditor
              key={section.id}
              section={section}
              enabledModules={enabledModules.map(({ key, label }) => ({ key, label }))}
              onChange={(next) => editCustomSection(next)}
              onDelete={() => {
                queueOldHomeAsset(section.image_url);
                setHomeDraft((current) => deleteCustomHomeSection(current, section.id));
                setSaved(false);
              }}
            onDuplicate={() => { setHomeDraft((current) => duplicateCustomHomeSection(current, section.id)); setSaved(false); }}
            onMoveUp={() => { setHomeDraft((current) => moveCustomHomeSection(current, section.id, 'up')); setSaved(false); }}
            onMoveDown={() => { setHomeDraft((current) => moveCustomHomeSection(current, section.id, 'down')); setSaved(false); }}
            canMoveUp={canMoveCustomHomeSection(homeDraft, section.id, 'up')}
            canMoveDown={canMoveCustomHomeSection(homeDraft, section.id, 'down')}
            onUpload={(file) => { void uploadCustomSectionImage(section.id, file); }}
            />)}
            {isLegacyHome ? (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5">
                <p className="text-sm text-amber-100">Using the current legacy home layout.</p>
                <p className="mt-1 text-xs text-amber-200/70">Choose the recommended layout to enable event-specific home page controls. The current attendee experience stays unchanged until you save.</p>
                <Button className="mt-4" onClick={() => { setHomeDraft((current) => mergeEventHomeLayout(current, createRecommendedHomeLayout())); setSaved(false); }}>Use recommended adaptive layout</Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between rounded-xl border border-gray-800 bg-gray-900 p-4">
                  <div><p className="text-sm font-medium text-white">Adaptive home layout</p><p className="text-xs text-gray-500">Controls affect home blocks only; bottom navigation remains stable.</p></div>
                  <button type="button" className="text-xs text-gray-400 hover:text-white" onClick={() => { setHomeDraft(resetHomeLayout()); setSaved(false); }}>Reset to legacy</button>
                </div>
                {orderedHomeKeys.map((key, index) => {
                  const block = homeDraft.blocks?.[key] ?? {};
                  const dependencies: EventModuleKey[] = key === 'happening_now' || key === 'up_next' ? ['agenda'] : key === 'featured_speakers' ? ['speakers'] : key === 'featured_papers' ? ['papers'] : key === 'featured_exhibitors' ? ['exhibitors'] : key === 'recap' ? ['recap', 'ai_copilot'] : [];
                  const missingDependencies = dependencies.filter((dependency) => !isEventModuleEnabled(draft, dependency));
                  const visibility = block.visibility ?? recommendedHomeBlocks[key]?.visibility ?? ['before', 'during', 'after'];
                  return <article key={key} className="rounded-xl border border-gray-800 bg-gray-900 p-4">
                    <div className="flex items-start gap-3">
                      <input type="checkbox" aria-label={`Show ${homeBlockLabels[key]}`} checked={block.enabled ?? true} onChange={(e) => { setHomeDraft((current) => updateHomeBlock(current, key, { enabled: e.target.checked })); setSaved(false); }} className="mt-1 h-4 w-4 accent-sky-500" />
                      <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-white">{homeBlockLabels[key]}</p>
                        {missingDependencies.length > 0 && <p className="mt-1 text-xs text-amber-300">Requires enabled {missingDependencies.map((dependency) => getEventModuleLabel(draft, dependency)).join(' and ')} modules.</p>}
                        <label className="mt-3 block text-xs text-gray-400">Title<input aria-label={`${homeBlockLabels[key]} title`} value={block.title ?? homeBlockLabels[key]} maxLength={80} onChange={(e) => { setHomeDraft((current) => updateHomeBlock(current, key, { title: e.target.value })); setSaved(false); }} className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white" /></label>
                        <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-400">{(['before', 'during', 'after'] as EventPhase[]).map((phase) => { const lastSelectedPhase = visibility.length === 1 && visibility.includes(phase); return <label key={phase} className="flex items-center gap-1" title={lastSelectedPhase ? 'Each visible block must have at least one event phase.' : undefined}><input type="checkbox" checked={visibility.includes(phase)} disabled={lastSelectedPhase} onChange={(e) => { const next = e.target.checked ? [...visibility, phase] : visibility.filter((item) => item !== phase); setHomeDraft((current) => updateHomeBlock(current, key, { visibility: next })); setSaved(false); }} />{phase[0].toUpperCase() + phase.slice(1)}</label>; })}</div>
                        {key === 'announcements' && hasAnnouncements === false && <Link to={`/notifications?event_id=${event.id}`} className="mt-3 inline-flex text-xs text-sky-400 hover:text-sky-300">No announcements yet — create one in Notifications.</Link>}
                        {key === 'quick_actions' && <div className="mt-3 flex flex-wrap gap-3">{quickActionKeys.map((moduleKey) => { const checked = (block.module_keys ?? []).includes(moduleKey); const enabled = isEventModuleEnabled(draft, moduleKey); return <label key={moduleKey} className="flex items-center gap-1 text-xs text-gray-300"><input type="checkbox" aria-label={`${getEventModuleLabel(undefined, moduleKey)} quick action`} disabled={!enabled} checked={checked && enabled} onChange={() => { setHomeDraft((current) => toggleQuickAction(current, moduleKey)); setSaved(false); }} />{getEventModuleLabel(draft, moduleKey)}</label>; })}</div>}
                        {(key.startsWith('featured_') || key === 'announcements') && <label className="mt-3 block text-xs text-gray-400">Items to show<input type="number" min="1" max="10" value={block.limit ?? 3} onChange={(e) => { setHomeDraft((current) => updateHomeBlock(current, key, { limit: Number(e.target.value) })); setSaved(false); }} className="ml-2 w-16 rounded border border-gray-700 bg-gray-950 px-2 py-1 text-white" /></label>}
                      </div>
                      <div className="flex flex-col gap-1"><button type="button" aria-label={`Move ${homeBlockLabels[key]} up`} disabled={index === 0} onClick={() => { setHomeDraft((current) => moveHomeBlock(current, key, 'up')); setSaved(false); }} className="rounded p-1 text-gray-400 disabled:opacity-25"><ChevronUp size={15} /></button><button type="button" aria-label={`Move ${homeBlockLabels[key]} down`} disabled={index === orderedHomeKeys.length - 1} onClick={() => { setHomeDraft((current) => moveHomeBlock(current, key, 'down')); setSaved(false); }} className="rounded p-1 text-gray-400 disabled:opacity-25"><ChevronDown size={15} /></button></div>
                    </div>
                  </article>;
                })}
              </>
            )}
          </section>
          <aside className="h-fit space-y-3 xl:sticky xl:top-8"><div className="flex gap-1">{(['before', 'during', 'after'] as EventPhase[]).map((phase) => <button type="button" key={phase} onClick={() => setPreviewPhase(phase)} className={`rounded px-2 py-1 text-xs ${previewPhase === phase ? 'bg-sky-500 text-white' : 'bg-gray-800 text-gray-300'}`}>{phase[0].toUpperCase() + phase.slice(1)}</button>)}</div><HomeLayoutPreview event={event} layout={homeDraft} modules={draft} phase={previewPhase} /></aside>
        </div>
      )}
    <ConfirmDialog open={confirmSaveOpen} onClose={() => setConfirmSaveOpen(false)} onConfirm={save} loading={saving}
      intent="primary" title="Save changes?" description="Apply the current app setup changes?" confirmLabel="Save changes" />
    </div>
  );
}
